﻿import { Suspense, useEffect, useState, type ReactNode } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router';
import { selectedGuildId, setSelectedGuildId } from '../lib/api';
import { useAuth } from '../lib/auth';
import { loginHref } from '../lib/loginHref';
import { dict, PANEL_LANG_LABEL, PANEL_LANGS, useLang, useT, type PanelLang } from '../lib/lang';
import {
  CATEGORIES,
  CATEGORY_PREFIX,
  NAV,
  activeNavPath,
  categoryOfPath,
  fold,
  ownerScopeOf,
  type NavItem,
} from '../lib/nav';
import Landing from '../pages/public/Landing';
import { GuildChooser, GuildChooserLoading, initials, useGuilds } from './GuildChooser';
import { Icon } from './icons';
import { Button, buttonClass, cx, Spinner } from './ui';

/**
 * Chuỗi của khung điều hướng — xem `lib/lang.tsx` về vì sao đặt tại chỗ.
 *
 * Các mũi tên `←`/`→` đã được RÚT KHỎI chuỗi (14/08/2026) và vẽ bằng `<Icon>`
 * ngay cạnh nhãn. Chỉ làm ở đây và ở `pages/Category.tsx` — hai chỗ có phần
 * khung đang được viết lại. Hàng trăm mũi tên khác nằm trong chuỗi copy của các
 * trang vẫn để nguyên: chúng là NỘI DUNG, và động vào là phải quyết định lại vị
 * trí của mũi tên trong câu ở cả hai thứ tiếng, ở ba mươi file.
 */
const T = dict({
  en: {
    checkingSession: 'Checking your session…',
    adminHere: 'Administrator here',
    modHere: 'Moderator here',
    panelKicker: 'Admin panel',
    switchGuild: (count: number) => `Switch server (${count})`,
    ownerKicker: 'Bot owner',
    ownerScopeName: 'Across every server',
    ownerDoor: 'Bot owner area',
    backToGuild: 'Back to a server',
    searchPlaceholder: 'Search pages…',
    searchAria: 'Search pages in the menu',
    noMatch: (query: string) => `No page matches “${query}”.`,
    allSections: 'All sections',
    home: 'Home',
    back: 'Back',
    signOut: 'Sign out',
    openMenu: 'Open menu',
    themeToggle: 'Toggle light/dark theme',
    loginTitle: 'Bot admin panel',
    loginBody:
      'Sign in with your Discord account. Access is checked against your real roles in the server at sign-in time.',
    loginButton: 'Sign in with Discord',
    deniedTitle: 'Access denied',
    deniedBody: 'Your account does not have permission to open this admin panel.',
    tryAnother: 'Try another account',
  },
  vi: {
    checkingSession: 'Đang kiểm tra phiên đăng nhập…',
    adminHere: 'Quản trị viên ở đây',
    modHere: 'Kiểm duyệt viên ở đây',
    panelKicker: 'Bảng quản trị',
    switchGuild: (count: number) => `Đổi server (${count})`,
    ownerKicker: 'Chủ bot',
    ownerScopeName: 'Toàn bộ server',
    ownerDoor: 'Khu chủ bot',
    backToGuild: 'Về panel server',
    searchPlaceholder: 'Tìm trang…',
    searchAria: 'Tìm trang trong menu',
    noMatch: (query: string) => `Không có trang nào khớp “${query}”.`,
    allSections: 'Tất cả mục',
    home: 'Trang chủ',
    back: 'Quay lại',
    signOut: 'Đăng xuất',
    openMenu: 'Mở menu',
    themeToggle: 'Đổi giao diện sáng/tối',
    loginTitle: 'Bảng quản trị bot',
    loginBody:
      'Đăng nhập bằng tài khoản Discord của bạn. Quyền truy cập được kiểm tra theo vai trò thật trong máy chủ tại thời điểm đăng nhập.',
    loginButton: 'Đăng nhập bằng Discord',
    deniedTitle: 'Không có quyền truy cập',
    deniedBody: 'Tài khoản của bạn không đủ quyền mở bảng quản trị này.',
    tryAnother: 'Thử đăng nhập bằng tài khoản khác',
  },
});

/**
 * Khung điều hướng. Thanh trái cố định trên màn hình rộng, thu vào ngăn kéo
 * trượt trên máy tính bảng.
 *
 * ── Thanh bên mỗi lúc mở đúng MỘT nhóm (13/08/2026) ───────────────────────
 *
 * Bản trước bày phẳng cả 47 mục của 9 nhóm cùng lúc, với một cơ chế thu/mở tự
 * nhớ trong localStorage. Ở mốc đó menu dài hơn màn hình và người dùng phải
 * cuộn để tìm — mà "không nhớ nó nằm ở nhóm nào" là vấn đề của một menu dài,
 * không phải của cách chia nhóm.
 *
 * Giờ thanh bên có hai hình dạng, và cả hai suy từ `location.pathname`:
 *
 *  • **chưa vào nhóm nào** (`/overview`) — danh sách 8 nhóm, mỗi nhóm một dòng;
 *  • **đang trong một nhóm** — dòng "← Tất cả mục", tên nhóm, rồi các trang của
 *    riêng nó.
 *
 * Cơ chế nhớ-nhóm-đã-thu biến mất cùng bản trước: khi mỗi lúc chỉ có 0 hoặc 1
 * nhóm mở và nó LUÔN là nhóm đúng, không còn gì để mà nhớ.
 *
 * Ô tìm trang giữ nguyên chỗ cũ và là lối tắt một bước cho người đã thuộc bản
 * đồ — đang tìm thì thanh bên bỏ hẳn mô hình nhóm, vẽ phẳng mọi kết quả kèm tên
 * nhóm của từng cái.
 */

/**
 * Cổng đăng nhập. Không chứa state nào, và điều đó có chủ ý.
 *
 * Mọi thứ cần tới phiên nằm ở `PanelShell` — một component ở CẤP MODULE. Đặt nó
 * lồng bên trong `Layout` thì mỗi lần `Layout` vẽ lại sẽ sinh ra một kiểu
 * component MỚI, và React coi đó là một cây khác: nó tháo cả cây cũ rồi dựng
 * lại từ đầu. Hệ quả thấy được ngay là gõ một chữ vào ô tìm trang cũng thổi bay
 * toàn bộ trạng thái của trang đang mở và bắn lại mọi request của nó.
 */
export function Layout(): React.JSX.Element {
  const { user, loading } = useAuth();
  const t = useT(T);
  const location = useLocation();

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Spinner label={t.checkingSession} />
      </div>
    );
  }

  // Chưa đăng nhập thì MỌI địa chỉ panel hiện trang giới thiệu public thay vì
  // màn hình đăng nhập trần. Nút đăng nhập trên đó dùng `/api/auth/login-to`
  // nên sau khi đăng nhập vẫn quay về đúng địa chỉ đang đứng.
  if (user === null) return <Landing />;

  // Trang chủ tự dựng khung của nó (topbar public + lưới server), không có thanh
  // bên panel: ở đây chưa chọn server nào, mà cả thanh bên nói về "bạn đang thao
  // tác lên cộng đồng nào" — vẽ nó ra lúc câu trả lời chưa tồn tại là nói dối.
  if (location.pathname === '/') {
    return (
      <Suspense fallback={<Spinner />}>
        <Outlet />
      </Suspense>
    );
  }

  // Danh sách server chỉ được hỏi SAU khi đã có phiên: `/api/guilds` trả 401 khi
  // chưa đăng nhập, và `apiFetch` phản ứng với 401 bằng cách chuyển hướng sang
  // trang đăng nhập — tức gọi nó quá sớm là tự đá mình ra khỏi màn hình đăng nhập.
  return <PanelShell />;
}

/**
 * Khung panel: thanh bên + thanh ngang + vùng nội dung.
 *
 * Xuất ra ngoài để `preview/views.tsx` dựng được nó mà không phải đi qua
 * `Layout` (vốn tự quyết định giữa trang giới thiệu, trang chủ và khung panel
 * theo phiên đăng nhập và địa chỉ). Cùng lý do với `CategoryView` — xem chú
 * thích ở `pages/Category.tsx`.
 */
export function PanelShell(): React.JSX.Element {
  const { user, guildName, isAdmin, isBotOwner, logout } = useAuth();
  const { lang } = useLang();
  const t = useT(T);
  const { guilds, loading: loadingGuilds, error } = useGuilds();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  /**
   * Server đang mở. Phải là state chứ không đọc thẳng `selectedGuildId()`: nguồn
   * thật nằm ở localStorage, mà thay đổi trong đó không làm React vẽ lại.
   */
  const [picked, setPicked] = useState<string>(selectedGuildId);
  /** true = người dùng đang chủ động ĐỔI server, không phải lần chọn đầu. */
  const [switching, setSwitching] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  // Đóng ngăn kéo sau mỗi lần điều hướng — trên máy tính bảng nó che hết nội dung.
  useEffect(() => setOpen(false), [location.pathname]);

  const known = guilds.some((guild) => guild.id === picked);

  /**
   * Chốt lựa chọn về server DUY NHẤT khi tài khoản chỉ quản một cộng đồng.
   *
   * Bắt họ bấm qua một màn hình một-lựa-chọn mỗi lần đăng nhập là thu phí một cú
   * bấm để đổi lấy thông tin họ đã biết — tên server vẫn hiện ở thanh bên. Từ
   * hai server trở lên thì phải hỏi: mọi trang đều thuộc về một cộng đồng cụ
   * thể, và những trang GHI (trao thưởng, reset XP) mà nhầm server là nhầm
   * người thật.
   *
   * Trong `useEffect` chứ không ngay trong thân hàm: gọi `setState` giữa lượt vẽ
   * của chính component đó là một vòng vẽ lại thừa, và React cảnh báo đúng như vậy.
   */
  useEffect(() => {
    if (loadingGuilds || known || guilds.length !== 1) return;
    const only = guilds[0]?.id ?? '';
    setSelectedGuildId(only);
    setPicked(only);
  }, [loadingGuilds, known, guilds]);

  const activeTo = activeNavPath(location.pathname);
  /** Nhóm đang mở — SUY từ địa chỉ, xem `lib/nav.ts`. `null` = chưa vào nhóm nào. */
  const openCategory = categoryOfPath(location.pathname);

  const needle = fold(query.trim());
  // Tìm trên CẢ HAI bản dịch, không chỉ bản đang hiện: người quen menu tiếng
  // Việt gõ "phan thuong" vẫn phải ra "Reward settings" khi panel đang tiếng Anh.
  const matches = (item: NavItem): boolean =>
    needle.length === 0 ||
    fold(`${item.label.en} ${item.label.vi} ${item.keywords ?? ''}`).includes(needle);

  /**
   * Tài khoản này có được thấy mục đó không.
   *
   * MỘT hàm cho cả hai chỗ lọc (danh sách nhóm, và câu "không có trang nào
   * khớp"). Hai biểu thức viết rời từng là hai bản phủ định của nhau, và một
   * điều kiện thêm vào chỉ một bên sẽ cho ra menu rỗng kèm dòng chữ nói rằng có
   * kết quả — hoặc ngược lại.
   *
   * KHÔNG phải lớp bảo vệ: máy chủ kiểm lại ở từng endpoint.
   */
  const allowed = (item: NavItem): boolean =>
    (item.adminOnly !== true || isAdmin) &&
    (item.ownerOnly !== true || isBotOwner) &&
    (item.allowedUserIds === undefined || (user !== null && item.allowedUserIds.includes(user.id)));

  /**
   * Đang đứng trong khu chủ bot hay trong panel của một server.
   *
   * Kèm `isBotOwner` chứ không chỉ hỏi địa chỉ: người không phải chủ bot mà mở
   * `/owner` (dấu trang cũ, link được gửi) sẽ nhận 403 từ máy chủ, và lúc đó
   * thanh bên phải vẫn là menu server của họ — chuyển họ sang một phạm vi rỗng
   * là bỏ họ lại giữa màn hình không lối ra.
   */
  const ownerMode = isBotOwner && ownerScopeOf(location.pathname);

  /**
   * Hai phạm vi loại trừ nhau: thanh bên chỉ vẽ các nhóm thuộc phạm vi đang mở.
   *
   * Ở phạm vi server, nhóm "Chủ bot" bị ẩn KỂ CẢ với chủ bot. Nó từng nằm cuối
   * menu của mọi server, và điều đó nói sai về chính nó: mọi mục trong đó nhìn
   * ngang qua tất cả cộng đồng, nên đặt chúng dưới tên một server cụ thể là mời
   * người ta đọc số liệu toàn hệ thống thành số liệu của server đang mở. Lối vào
   * nằm ở màn hình Đổi server và ở trang chủ — nơi câu hỏi "phạm vi nào" đang
   * được hỏi sẵn.
   */
  const groups = NAV.filter((group) => (group.ownerScope === true) === ownerMode);

  /** Nhóm có ít nhất một mục tài khoản này thấy được — nhóm rỗng thì biến mất. */
  const visibleCategories = CATEGORIES.filter(
    (category) => (category.ownerScope === true) === ownerMode,
  ).filter((category) => category.items.some(allowed));

  /** Mọi mục khớp ô tìm, phẳng, kèm nhóm của từng cái. */
  const found =
    needle.length === 0
      ? []
      : groups.flatMap((group) =>
          group.items
            .filter((item) => allowed(item) && matches(item))
            .map((item) => ({ item, group })),
        );

  if (loadingGuilds || error !== null) return <GuildChooserLoading error={error} />;
  // Chỉ một server và `useEffect` ở trên chưa kịp chạy — một nhịp, không nháy.
  if (!known && guilds.length === 1) return <GuildChooserLoading />;

  // Chưa chọn bao giờ, chọn phải server không còn quyền, hoặc đang chủ động đổi.
  if (!known || switching) {
    return (
      <GuildChooser
        guilds={guilds}
        current={known ? picked : ''}
        onPick={(guildId) => {
          setSelectedGuildId(guildId);
          setSwitching(false);
          setPicked(guildId);

          // Đang đứng trong khu chủ bot mà chọn một server = muốn ra khỏi khu
          // đó. Chỉ `setSwitching(false)` thì địa chỉ vẫn là `/owner/...`, mà
          // phạm vi suy từ địa chỉ — nên màn hình đóng lại và mọi thứ y như cũ,
          // trông hệt như cú bấm không ăn. Về thẳng trang Tổng quan của server
          // vừa chọn.
          if (ownerScopeOf(location.pathname)) {
            window.location.href = '/overview';
            return;
          }

          // Nạp lại CẢ TRANG, kể cả ở lần chọn đầu tiên. Lý do thứ hai mới là
          // lý do bắt buộc — xem chú thích cùng nội dung ở `pages/Home.tsx`:
          // `isAdmin` tính theo server đang chọn từ một phản hồi đã nằm trong
          // cache, nên không nạp lại thì menu vẽ theo cấp quyền của server
          // trước, hoặc theo một cấp quyền rỗng ở lần chọn đầu.
          window.location.reload();
        }}
        {...(isBotOwner
          ? {
              onOwner: (): void => {
                /**
                 * Khu chủ bot không thuộc server nào — nhưng panel thì vẫn cần
                 * MỘT server để vẽ phần còn lại của thanh bên, và màn hình này
                 * chỉ nhường chỗ khi `picked` nằm trong danh sách. Chưa chọn
                 * bao giờ thì chốt tạm server đầu tiên: đổi lại bằng đúng màn
                 * hình này, và không có nó thì cú bấm vào thẻ Chủ bot dẫn về
                 * chính màn hình vừa bấm.
                 */
                if (!known) {
                  const first = guilds[0]?.id ?? '';
                  if (first !== '') {
                    setSelectedGuildId(first);
                    setPicked(first);
                  }
                }
                setSwitching(false);
                void navigate('/owner');
              },
            }
          : {})}
        {...(known ? { onCancel: () => setSwitching(false) } : {})}
      />
    );
  }

  // Phiên hết hạn giữa chừng: `useAuth` trả `null` trong khi `PanelShell` vẫn
  // đang gắn. Không có nhánh này thì mọi chỗ đọc `user.` bên dưới đều nổ.
  if (user === null) return <LoginScreen />;
  const account = user;

  const here = guilds.find((guild) => guild.id === picked);
  const currentName = here?.name ?? guildName;
  const levelHere =
    here?.level === undefined || here.level === null
      ? null
      : here.level === 'admin'
        ? t.adminHere
        : t.modHere;

  return (
    <div className="flex min-h-screen bg-app">
      {open && (
        <div
          className="fixed inset-0 z-30 bg-overlay backdrop-blur-sm lg:hidden"
          onClick={() => setOpen(false)}
          aria-hidden
        />
      )}

      <aside
        className={cx(
          'fixed inset-y-0 left-0 z-40 flex w-64 flex-col border-r border-border bg-surface transition-transform',
          'lg:translate-x-0',
          open ? 'translate-x-0' : '-translate-x-full',
        )}
      >
        {/*
          Đầu thanh bên nói ra server đang mở, LUÔN LUÔN — kể cả khi tài khoản
          chỉ quản một cộng đồng. Bản trước giấu ô chọn khi danh sách có một mục,
          và vì đó là chỗ duy nhất nhắc tên server nên với những người đó panel
          không bao giờ nói nó đang phục vụ ai.
        */}
        {/*
          Hàng thương hiệu — góc trên bên trái của cả panel.

          ── Vì sao GIỜ có thanh ngang, sau khi bản trước từ chối nó (14/08/2026) ──

          Lập luận cũ vẫn đúng ở phần lập luận: hai chỗ cùng khẳng định "bạn đang
          ở đâu" là hai chỗ sẽ lệch nhau. Cái sai nằm ở KẾT LUẬN — nó cấm cả
          thanh ngang, trong khi thứ đáng cấm chỉ là NÓI HAI LẦN cùng một điều.

          Nên bản này chia việc, và mỗi câu hỏi chỉ còn đúng một chỗ trả lời:

            • thanh bên trả lời "đi đâu được" — thương hiệu, phạm vi đang mở, ô
              tìm trang, danh sách trang;
            • thanh ngang trả lời "đang ở đâu, và tôi là ai" — dải đường dẫn,
              chủ đề sáng/tối, ngôn ngữ, tài khoản.

          Tài khoản và hai nút chuyển rời khỏi CHÂN thanh bên vì đúng phép chia
          đó: không cái nào là một nơi để ĐI TỚI. Nằm lẫn dưới đáy menu, mỗi lần
          đổi chủ đề là một lần mắt phải quét hết chiều cao của cả bản đồ để tìm
          một cái nút không thuộc về bản đồ.

          Ô tìm trang Ở LẠI thanh bên, và đó là chỗ duy nhất của lượt sửa này
          không theo phép chia trên: kết quả tìm vẽ ra ngay trong <nav> bên dưới
          nó, kèm tên nhóm của từng trang. Kéo ô nhập lên trên mà để kết quả nằm
          dưới-trái là đặt câu hỏi ở một chỗ và câu trả lời ở một chỗ khác.

          ── Hình học ──────────────────────────────────────────────────────────

          Thanh ngang nằm TRONG cột nội dung (`sticky top-0`), không trải hết bề
          ngang màn hình. Nhờ vậy phép tính `w-64` / `lg:ml-64` giữ nguyên và
          không chỗ nào phải bù `padding-top`. Hàng thương hiệu này cao đúng bằng
          nó (`h-14`) và dùng chung một đường viền dưới, nên hai khối vẫn đọc ra
          là MỘT dải liền mạch chứ không phải hai tầng chồng lên nhau.

          Thanh ngang đứng DƯỚI lớp phủ của ngăn kéo (z-20 < z-30): mở menu trên
          điện thoại thì nó phải mờ đi cùng phần còn lại, chứ không nổi lên như
          một thứ vẫn bấm được.
        */}
        <Link
          to="/"
          className="flex h-14 shrink-0 items-center gap-2 border-b border-border px-4 text-sm font-semibold tracking-heading text-fg transition-colors hover:bg-surface-2"
        >
          <span className="text-accent" aria-hidden>
            <Icon name="robot" size={18} />
          </span>
          GenSolva
        </Link>

        <div
          className={cx(
            'border-b border-border',
            // Khu chủ bot đổi màu nền cả phần đầu, cùng tông hổ phách với thẻ ở
            // màn hình Đổi server. Đây là phần thay cho việc đọc kỹ tên phạm vi:
            // hai chế độ trông KHÁC nhau ngay từ khoé mắt, nên không ai kịp gõ
            // vào một trang toàn hệ thống khi tưởng mình đang ở trong một server.
            ownerMode && 'bg-owner-soft',
          )}
        >
          {/*
            Hàng server — bấm vào là về trang Tổng quan CỦA CHÍNH server đó.

            Ở khu chủ bot nó không phải một link: phạm vi đó không có "server
            đang mở", nên một cú bấm dẫn tới trang tổng quan của một cộng đồng cụ
            thể là đưa người ta ra khỏi phạm vi họ đang làm việc mà không báo.
          */}
          {ownerMode ? (
            <div className="flex items-center gap-2.5 px-4 py-3">
              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-owner-soft text-owner">
                <Icon name="crown" size={17} />
              </span>
              <span className="min-w-0">
                <span className="block text-[0.65rem] uppercase tracking-label text-fg-subtle">
                  {t.ownerKicker}
                </span>
                <span className="block truncate text-sm font-semibold text-fg">
                  {t.ownerScopeName}
                </span>
              </span>
            </div>
          ) : (
            <Link
              to="/overview"
              className="flex items-center gap-2.5 px-4 py-3 transition-colors hover:bg-surface-2"
              title={currentName}
            >
              {here?.iconUrl != null ? (
                <img src={here.iconUrl} alt="" className="h-8 w-8 shrink-0 rounded-full" />
              ) : (
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-surface-3 text-[0.7rem] font-semibold text-fg-muted">
                  {initials(currentName.length > 0 ? currentName : 'GenSolva')}
                </span>
              )}
              <span className="min-w-0">
                <span className="block text-[0.65rem] uppercase tracking-label text-fg-subtle">
                  {t.panelKicker}
                </span>
                <span className="block truncate text-sm font-semibold text-fg">
                  {currentName.length > 0 ? currentName : 'GenSolva'}
                </span>
              </span>
            </Link>
          )}

          <div className="px-4 pb-3">
            {/*
            Nút này là cửa DUY NHẤT giữa hai phạm vi, nên nó không được phép vắng
            mặt ở bất kỳ tổ hợp nào:

              • đang ở khu chủ bot → lối ra, luôn hiện;
              • chủ bot đang xem một server → lối vào, kể cả khi họ chỉ có MỘT
                cộng đồng. Bản đầu chỉ hiện nút khi `guilds.length > 1`, và với
                một chủ bot một-server thì nhóm "Chủ bot" vừa bị ẩn khỏi menu
                trong khi cửa vào cũng không tồn tại — khu đó biến mất khỏi panel;
              • quản trị viên nhiều server → vẫn là nút đổi server như cũ.
          */}
            {(ownerMode || guilds.length > 1 || isBotOwner) && (
              <button
                type="button"
                onClick={() => setSwitching(true)}
                className={cx(
                  'inline-flex items-center gap-1 text-xs hover:underline',
                  ownerMode ? 'text-owner' : 'text-accent',
                )}
              >
                {ownerMode && <Icon name="arrow-left" size={12} />}
                {ownerMode
                  ? t.backToGuild
                  : guilds.length > 1
                    ? t.switchGuild(guilds.length)
                    : t.ownerDoor}
                {!ownerMode && <Icon name="arrow-right" size={12} />}
              </button>
            )}
          </div>
        </div>

        {/*
          Ô tìm trang. Với hơn ba mươi mục, đây là đường ngắn nhất tới một trang
          mà người dùng biết tên nhưng không nhớ nó nằm ở nhóm nào — và "không
          nhớ nó ở nhóm nào" đúng là vấn đề của một menu dài, chứ không phải
          "nhóm chia sai".
        */}
        <div className="border-b border-border px-3 py-2">
          <div className="relative">
            <span
              className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-fg-subtle"
              aria-hidden
            >
              <Icon name="magnifying-glass" size={13} />
            </span>
            <input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder={t.searchPlaceholder}
              aria-label={t.searchAria}
              className="w-full rounded-md border border-border-strong bg-field py-1.5 pl-8 pr-2 text-xs text-fg placeholder:text-fg-subtle focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
            />
          </div>
        </div>

        <nav className="flex-1 space-y-1 overflow-y-auto px-2 py-3 pb-6">
          {/*
            Đang TÌM: bỏ hẳn mô hình nhóm, vẽ phẳng mọi kết quả kèm tên nhóm của
            từng cái. Cột tên nhóm không phải trang trí — nó dạy người dùng bản đồ
            mới ngay trong lúc họ đang dùng lối tắt để đi vòng qua bản đồ đó.
          */}
          {needle.length > 0 &&
            found.map(({ item, group }) => (
              <NavRow
                key={item.to}
                item={item}
                lang={lang}
                active={item.to === activeTo}
                note={group.label[lang]}
              />
            ))}

          {/* `found` dựng từ `groups`, không phải `NAV`: ô tìm chỉ tìm trong phạm
              vi đang mở, nên câu "không có trang nào khớp" cũng phải hỏi đúng
              phạm vi đó. Hỏi cả `NAV` thì một từ khoá chỉ khớp mục ở phạm vi bên
              kia cho ra menu rỗng mà không một dòng chữ nào giải thích. */}
          {needle.length > 0 && found.length === 0 && (
            <p className="px-3 py-4 text-center text-xs text-fg-subtle">{t.noMatch(query)}</p>
          )}

          {/* CHƯA vào nhóm nào: mục Tổng quan, rồi danh sách nhóm. */}
          {needle.length === 0 && openCategory === null && (
            <>
              {!ownerMode &&
                NAV[0]?.items.map((item) => (
                  <NavRow key={item.to} item={item} lang={lang} active={item.to === activeTo} />
                ))}

              <div className="pt-2">
                {visibleCategories.map((category) => (
                  <Link
                    key={category.slug}
                    to={`${CATEGORY_PREFIX}/${category.slug}`}
                    className="group flex items-center gap-2.5 rounded-lg px-3 py-2 text-sm text-fg-muted transition-colors hover:bg-surface-2 hover:text-fg"
                  >
                    <span className="shrink-0 text-fg-subtle transition-colors group-hover:text-accent">
                      <Icon name={category.icon} size={16} />
                    </span>
                    <span className="min-w-0 flex-1 truncate">{category.label[lang]}</span>
                    <span aria-hidden className="shrink-0 text-fg-subtle/60">
                      <Icon name="caret-right" size={11} />
                    </span>
                  </Link>
                ))}
              </div>
            </>
          )}

          {/* ĐANG trong một nhóm: lối ra, tên nhóm, rồi các trang của riêng nó. */}
          {needle.length === 0 && openCategory !== null && (
            <>
              {/*
                Lối ra KHÔNG hiện ở khu chủ bot: ở đó chỉ có đúng một nhóm, nên
                "tất cả mục" sẽ dẫn về một danh sách một dòng. Lối ra của phạm vi
                đó là nút "← Về panel server" ở đầu thanh bên.
              */}
              {!ownerMode && (
                <Link
                  to="/overview"
                  className="flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs text-fg-subtle transition-colors hover:bg-surface-2 hover:text-fg"
                >
                  <Icon name="arrow-left" size={12} />
                  {t.allSections}
                </Link>
              )}

              <Link
                to={`${CATEGORY_PREFIX}/${openCategory.slug}`}
                aria-current={location.pathname.startsWith(CATEGORY_PREFIX) ? 'page' : undefined}
                className="mt-1 flex items-center gap-2 rounded-lg px-3 py-1.5 text-[0.68rem] font-semibold uppercase tracking-label text-fg-muted transition-colors hover:bg-surface-2 hover:text-fg"
              >
                <span className="text-accent">
                  <Icon name={openCategory.icon} size={14} />
                </span>
                {openCategory.label[lang]}
              </Link>

              <div className="space-y-0.5">
                {openCategory.items.filter(allowed).map((item, at, shown) => (
                  <div key={item.to}>
                    {/* Vạch ngăn TRƯỚC mục cấu hình đầu tiên của nhóm: nó tách
                        "chỗ làm việc" khỏi "chỗ chỉnh", mà không phải đẩy phần
                        chỉnh sang một nhóm khác. */}
                    {item.setting === true && at > 0 && shown[at - 1]?.setting !== true && (
                      <div className="mx-3 my-1 border-t border-dashed border-border" />
                    )}
                    <NavRow item={item} lang={lang} active={item.to === activeTo} />
                  </div>
                ))}
              </div>
            </>
          )}
        </nav>
      </aside>

      {/*
        KHÔNG thêm `overflow-*` vào cột này hay vào <main>.

        `StickyActions` (thanh Lưu bám đáy ở bảy trang cấu hình) là `sticky`, và
        `sticky` chết lặng lẽ khi có một tổ tiên mang `overflow`. Một thanh ngang
        `sticky` là anh em cùng cấp thì không sao; một `overflow-y-auto` thêm vào
        đây "cho gọn thanh cuộn" thì có.
      */}
      <div className="flex min-w-0 flex-1 flex-col lg:ml-64">
        <header
          className={cx(
            'sticky top-0 z-20 flex h-14 shrink-0 items-center gap-2 border-b border-border bg-surface/85 px-4 backdrop-blur',
            // Sợi hổ phách chạy hết bề ngang cột nội dung khi đang ở khu chủ
            // bot. Vô ngôn, và thấy được từ khoé mắt — đúng việc mà chú thích ở
            // khối phạm vi bên thanh bên nói màu này sinh ra để làm. Phần CHỮ
            // khẳng định phạm vi vẫn chỉ có một chỗ: dải đường dẫn ngay dưới đây.
            ownerMode && 'border-t-2 border-t-owner',
          )}
        >
          <Button
            variant="ghost"
            className="-ml-1 px-2 lg:hidden"
            onClick={() => setOpen(true)}
            aria-label={t.openMenu}
          >
            <Icon name="list" size={18} />
          </Button>

          {/*
            Trên điện thoại thanh bên bị thu vào ngăn kéo, nên logo và tên server
            phải lên đây — nếu không thì suốt phiên làm việc không có gì nói người
            dùng đang thao tác lên cộng đồng nào, và không có lối về trang chủ.
          */}
          <Link to="/" className="shrink-0 text-accent lg:hidden" aria-label={t.home}>
            <Icon name="robot" size={18} />
          </Link>
          {ownerMode ? (
            <span className="flex min-w-0 items-center gap-1.5 truncate text-sm font-medium text-fg lg:hidden">
              <Icon name="crown" size={14} className="shrink-0 text-owner" />
              {t.ownerScopeName}
            </span>
          ) : (
            <Link to="/overview" className="min-w-0 truncate text-sm font-medium text-fg lg:hidden">
              {currentName}
            </Link>
          )}

          {/* Dải đường dẫn — câu trả lời "đang ở đâu", và nó chỉ có ở đây. */}
          <div className="hidden min-w-0 flex-1 lg:block">
            <Crumbs
              pathname={location.pathname}
              lang={lang}
              ownerMode={ownerMode}
              serverName={currentName}
              t={t}
            />
          </div>

          <div className="ml-auto flex shrink-0 items-center gap-1">
            <ThemeToggle />
            <LanguageToggle />

            <span className="mx-1.5 hidden h-6 w-px bg-border sm:block" aria-hidden />

            {account.avatarUrl !== null ? (
              <img src={account.avatarUrl} alt="" className="h-7 w-7 shrink-0 rounded-full" />
            ) : (
              <div className="h-7 w-7 shrink-0 rounded-full bg-surface-3" />
            )}
            <div className="ml-1.5 hidden min-w-0 leading-tight md:block">
              <p className="truncate text-xs font-medium text-fg">{account.displayName}</p>
              {/*
                Cấp quyền hiện ở đây là cấp Ở SERVER ĐANG MỞ, không phải cấp cao
                nhất trên mọi server: một người là quản trị viên ở cộng đồng này
                và kiểm duyệt viên ở cộng đồng kia sẽ thấy menu khác nhau, nên
                dòng này phải giải thích được vì sao.
              */}
              <p className="truncate text-[0.68rem] text-fg-subtle">
                {levelHere ?? account.levelLabel}
              </p>
            </div>

            <Button
              variant="ghost"
              className="ml-1 px-2"
              onClick={() => void logout()}
              aria-label={t.signOut}
              title={t.signOut}
            >
              <Icon name="sign-out" size={16} />
            </Button>
          </div>
        </header>

        <main className="flex-1 p-4 sm:p-6">
          <Suspense fallback={<Spinner />}>
            <Outlet />
          </Suspense>
        </main>
      </div>
    </div>
  );
}

/** Một dòng trong thanh bên. Ba chỗ gọi, nên nó là một component chứ không phải JSX chép ba lần. */
function NavRow({
  item,
  lang,
  active,
  note,
}: {
  item: NavItem;
  lang: PanelLang;
  active: boolean;
  /** Tên nhóm, chỉ hiện ở kết quả tìm kiếm. */
  note?: string;
}): React.JSX.Element {
  return (
    <Link
      to={item.to}
      aria-current={active ? 'page' : undefined}
      className={cx(
        'flex items-center gap-2 rounded-lg px-3 py-1.5 text-sm transition-colors',
        active
          ? 'bg-accent-soft font-medium text-accent-soft-fg'
          : 'text-fg-muted hover:bg-surface-2 hover:text-fg',
        item.setting === true && !active && 'text-fg-subtle',
      )}
    >
      {item.setting === true && (
        <span aria-hidden className="shrink-0 opacity-60">
          <Icon name="gear-six" size={12} />
        </span>
      )}
      <span className="min-w-0 flex-1 truncate">{item.label[lang]}</span>
      {note !== undefined && note.length > 0 && (
        <span className="shrink-0 text-[0.65rem] text-fg-subtle">{note}</span>
      )}
    </Link>
  );
}

/**
 * Dải đường dẫn phía trên nội dung, kèm mũi tên lùi một bước.
 *
 * ── Vì sao KHÔNG dùng `history.back()` ────────────────────────────────────
 *
 * Rất nhiều lượt mở panel bắt đầu bằng một link dán trong Discord: người ta tới
 * thẳng `/rewards/queue` và không có "bước trước" nào trong lịch sử trình duyệt.
 * `history.back()` với họ là văng ra khỏi trang. Suy chuỗi từ `pathname` thì mũi
 * tên luôn trỏ về đúng bậc trên, dù tới đây bằng đường nào.
 *
 * Từ 14/08/2026 nó nằm trong THANH NGANG chứ không còn ở đầu vùng nội dung. Nó
 * là câu trả lời cho "đang ở đâu", và câu đó thuộc về thanh ngang theo đúng
 * phép chia việc ghi ở hàng thương hiệu. Đổi chỗ cũng lấy lại được một dòng
 * chiều dọc ở MỌI trang của panel.
 *
 * Vì thanh ngang chỉ hiện nó từ `lg` trở lên, phần rút gọn "chỉ mũi tên và một
 * nhãn" cho màn hình hẹp đã bỏ: dưới `lg` thanh ngang đang bày tên server và
 * nút menu, và nhét thêm một dải đường dẫn vào đó là ba thứ tranh nhau một hàng.
 */
function Crumbs({
  pathname,
  lang,
  ownerMode,
  serverName,
  t,
}: {
  pathname: string;
  lang: PanelLang;
  ownerMode: boolean;
  serverName: string;
  t: { home: string; back: string; ownerScopeName: string };
}): React.JSX.Element | null {
  const category = categoryOfPath(pathname);
  const activeTo = activeNavPath(pathname);
  const item = category?.items.find((candidate) => candidate.to === activeTo) ?? null;

  const trail: { to: string; label: ReactNode }[] = [{ to: '/', label: t.home }];
  if (ownerMode) {
    trail.push({
      to: `${CATEGORY_PREFIX}/owner`,
      label: (
        <span className="inline-flex items-center gap-1">
          <Icon name="crown" size={12} className="text-owner" />
          {t.ownerScopeName}
        </span>
      ),
    });
  } else {
    trail.push({ to: '/overview', label: serverName.length > 0 ? serverName : 'GenSolva' });
  }
  if (category !== null && !ownerMode) {
    trail.push({
      to: `${CATEGORY_PREFIX}/${category.slug}`,
      label: (
        <span className="inline-flex items-center gap-1">
          <Icon name={category.icon} size={12} />
          {category.label[lang]}
        </span>
      ),
    });
  }
  if (item !== null) trail.push({ to: item.to, label: item.label[lang] });

  // Hai bậc là ít nhất còn có nghĩa: ở `/overview` nó cho mũi tên về trang chủ,
  // tức bậc lùi mà mọi bước khác đều có. Một bậc thì không còn gì để lùi.
  if (trail.length < 2) return null;

  const parent = trail[trail.length - 2];
  const here = trail[trail.length - 1];
  if (parent === undefined || here === undefined) return null;

  return (
    <nav className="flex min-w-0 items-center gap-1.5 text-xs text-fg-subtle">
      <Link
        to={parent.to}
        aria-label={t.back}
        className="shrink-0 rounded p-1 transition-colors hover:bg-surface-2 hover:text-fg"
      >
        <Icon name="arrow-left" size={13} />
      </Link>
      {trail.slice(0, -1).map((crumb) => (
        <span key={crumb.to} className="flex shrink-0 items-center gap-1.5">
          <Link to={crumb.to} className="truncate transition-colors hover:text-fg hover:underline">
            {crumb.label}
          </Link>
          <span aria-hidden className="text-fg-subtle/60">
            /
          </span>
        </span>
      ))}
      <span className="truncate text-fg">{here.label}</span>
    </nav>
  );
}

/**
 * Lựa chọn giao diện sáng/tối, lưu vào localStorage.
 *
 * Đọc và áp dụng ngay trong `main.tsx` trước khi React vẽ, nên không có khoảnh
 * khắc trang loé sáng rồi mới chuyển sang tối.
 */
/**
 * Đổi ngôn ngữ panel, lưu vào localStorage.
 *
 * Nhãn nút là tên ngôn ngữ SẼ chuyển sang, không phải ngôn ngữ đang dùng: một
 * nút ghi "English" trong lúc panel đang tiếng Anh thì không ai biết bấm vào sẽ
 * ra gì. Chỉ hai thứ tiếng nên bấm là đảo — không cần menu.
 */
export function LanguageToggle(): React.JSX.Element {
  const { lang, setLang } = useLang();
  const next: PanelLang = PANEL_LANGS[(PANEL_LANGS.indexOf(lang) + 1) % PANEL_LANGS.length] ?? 'en';
  const title =
    lang === 'vi' ? `Chuyển sang ${PANEL_LANG_LABEL[next]}` : `Switch to ${PANEL_LANG_LABEL[next]}`;

  return (
    <Button
      variant="ghost"
      className="px-2 font-mono text-xs"
      onClick={() => setLang(next)}
      aria-label={title}
      title={title}
    >
      {next === 'vi' ? 'VI' : 'EN'}
    </Button>
  );
}

/**
 * Đổi chủ đề sáng/tối, lưu vào localStorage.
 *
 * Khoá `panel-theme` dùng chung với script inline trong `index.html` — chỗ áp
 * chủ đề trước khi trình duyệt vẽ khung hình đầu tiên. Đổi tên khoá thì phải
 * đổi cả hai chỗ; không có chỗ thứ ba.
 *
 * Hình là chủ đề SẼ chuyển sang, không phải chủ đề đang dùng — cùng quy ước với
 * nút đổi ngôn ngữ ngay cạnh nó.
 */
export function ThemeToggle(): React.JSX.Element {
  const t = useT(T);
  const [dark, setDark] = useState(() => document.documentElement.classList.contains('dark'));

  const toggle = (): void => {
    const next = !dark;
    setDark(next);
    document.documentElement.classList.toggle('dark', next);
    localStorage.setItem('panel-theme', next ? 'dark' : 'light');
  };

  return (
    <Button
      variant="ghost"
      className="px-2"
      onClick={toggle}
      aria-label={t.themeToggle}
      title={t.themeToggle}
    >
      <Icon name={dark ? 'sun' : 'moon'} size={16} />
    </Button>
  );
}

function LoginScreen(): React.JSX.Element {
  const t = useT(T);
  return (
    <CenteredCard>
      <h1 className="text-xl font-semibold tracking-heading text-fg">{t.loginTitle}</h1>
      <p className="mt-2 text-sm leading-relaxed text-fg-muted">{t.loginBody}</p>
      <a
        href={loginHref(window.location.pathname)}
        className={buttonClass('primary', 'mt-5 w-full px-4 py-2.5')}
      >
        {t.loginButton}
      </a>
    </CenteredCard>
  );
}

/** Trang từ chối truy cập — lý do do máy chủ đưa xuống qua query string. */
export function DeniedScreen(): React.JSX.Element {
  const t = useT(T);
  const reason = new URLSearchParams(window.location.search).get('ly-do');

  return (
    <CenteredCard>
      <h1 className="flex items-center gap-2 text-xl font-semibold tracking-heading text-bad">
        <Icon name="prohibit" size={20} />
        {t.deniedTitle}
      </h1>
      <p className="mt-3 text-sm leading-relaxed text-fg-muted">
        {reason !== null && reason.length > 0 ? reason : t.deniedBody}
      </p>
      <a href="/api/auth/login" className={buttonClass('secondary', 'mt-5 w-full px-4 py-2.5')}>
        {t.tryAnother}
      </a>
    </CenteredCard>
  );
}

function CenteredCard({ children }: { children: ReactNode }): React.JSX.Element {
  return (
    <div className="flex min-h-screen items-center justify-center bg-app p-4">
      <div className="w-full max-w-md rounded-xl border border-border bg-surface p-6 shadow-card">
        {children}
      </div>
    </div>
  );
}
